import React, { useState } from 'react';
import { X, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const QuickQuoteForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    product: '',
    grade: '',
    quantity: '',
    unit: 'kg'
  });

  const products = [
    { value: 'copper-rods', label: 'Copper Rods (8mm)' },
    { value: 'ccr', label: 'CCR - Continuous Cast Rod (8mm)' },
    { value: 'bare-wires', label: 'Bare Copper Wires' },
    { value: 'submersible-wires', label: 'Submersible Wires (0.5-1.4mm)' },
    { value: 'earthing-wires', label: 'Earthing Copper Wires' },
    { value: 'copper-ingots', label: 'Copper Ingots (75-225kg)' },
    { value: 'copper-strips', label: 'Copper Strips' },
    { value: 'copper-scrap', label: 'Copper Scrap' }
  ];

  const grades = [
    { value: 'cc-grade', label: 'CC Grade' },
    { value: 'ccr-grade', label: 'CCR Grade' },
    { value: 'commercial-grade', label: 'Commercial Grade' },
    { value: 'custom', label: 'Custom Requirements' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.product) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/quote-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Quote request submitted! We will contact you shortly.');
        
        // Reset form and close
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            phone: '',
            product: '',
            grade: '',
            quantity: '',
            unit: 'kg'
          });
          onClose();
        }, 1500);
      } else {
        toast.error('Failed to submit request. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting quote request:', error);
      toast.error('Failed to submit request. Please try again.');
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Modal */}
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="bg-gradient-to-br from-slate-950 to-slate-900 rounded-2xl border border-amber-600/30 shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="sticky top-0 bg-slate-950/95 backdrop-blur-sm border-b border-amber-600/20 p-6 flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-1">
                    Quick Quote <span className="text-amber-400">Request</span>
                  </h2>
                  <p className="text-gray-400 text-sm">Get instant pricing for your requirements</p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-slate-800 rounded-lg transition-colors"
                  aria-label="Close"
                >
                  <X className="w-6 h-6 text-gray-400 hover:text-white" />
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="p-6 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Name *
                    </label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Your name"
                      required
                      className="bg-slate-900 border-amber-600/20 focus:border-amber-500 text-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Phone *
                    </label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="Your phone number"
                      required
                      className="bg-slate-900 border-amber-600/20 focus:border-amber-500 text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    className="bg-slate-900 border-amber-600/20 focus:border-amber-500 text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Product *
                  </label>
                  <Select value={formData.product} onValueChange={(value) => setFormData({ ...formData, product: value })}>
                    <SelectTrigger className="bg-slate-900 border-amber-600/20 focus:border-amber-500 text-white">
                      <SelectValue placeholder="Select product" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-amber-600/30">
                      {products.map((product) => (
                        <SelectItem key={product.value} value={product.value} className="text-white hover:bg-slate-800">
                          {product.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Grade/Type
                  </label>
                  <Select value={formData.grade} onValueChange={(value) => setFormData({ ...formData, grade: value })}>
                    <SelectTrigger className="bg-slate-900 border-amber-600/20 focus:border-amber-500 text-white">
                      <SelectValue placeholder="Select grade" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-amber-600/30">
                      {grades.map((grade) => (
                        <SelectItem key={grade.value} value={grade.value} className="text-white hover:bg-slate-800">
                          {grade.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Quantity
                    </label>
                    <Input
                      type="number"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                      placeholder="Enter quantity"
                      className="bg-slate-900 border-amber-600/20 focus:border-amber-500 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Unit
                    </label>
                    <Select value={formData.unit} onValueChange={(value) => setFormData({ ...formData, unit: value })}>
                      <SelectTrigger className="bg-slate-900 border-amber-600/20 focus:border-amber-500 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-amber-600/30">
                        <SelectItem value="kg" className="text-white hover:bg-slate-800">KG</SelectItem>
                        <SelectItem value="ton" className="text-white hover:bg-slate-800">Ton</SelectItem>
                        <SelectItem value="pieces" className="text-white hover:bg-slate-800">Pieces</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-semibold py-6 rounded-lg transition-all duration-300 shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40"
                  >
                    Submit Request
                    <Send className="ml-2 w-5 h-5" />
                  </Button>
                  <Button
                    type="button"
                    onClick={onClose}
                    className="px-6 bg-slate-800 hover:bg-slate-700 text-white py-6 rounded-lg transition-colors"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default QuickQuoteForm;